/**
 * "Something was just saved" — told to every cache in the application.
 *
 * Several screens keep small reference lists (territories, positions,
 * categories, baskets) in memory so they are not fetched on every visit.
 * Until now nothing told those copies that a record had changed, so a new
 * territory or position appeared only after a hard refresh.
 *
 * Both HTTP clients call `announceDataChanged()` after any successful write
 * (POST, PUT, PATCH, DELETE). Every cache registers a clear-out with
 * `onDataChanged()`, and the next screen that asks fetches fresh data. The
 * announcement is coalesced to one per tick, so a burst of saves does not
 * trigger a burst of work.
 */
type Listener = () => void;

const listeners = new Set<Listener>();
let pending = false;

export function onDataChanged(fn: Listener): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** POSTs that only ask a question (a price preview, a check) change nothing. */
const READ_ONLY = /\/(preview|validate|check|calculate|quote|lookup|search)(\/|\?|$)/i;

export function announceDataChanged(path?: string) {
  if (path && READ_ONLY.test(path)) return;
  if (pending) return;
  pending = true;
  queueMicrotask(() => {
    pending = false;
    for (const fn of [...listeners]) {
      try { fn(); } catch { /* one cache failing to clear must not stop the rest */ }
    }
  });
}
